// Contratos do backoffice interno (fatia S5b).
import type { AccountKind, AccountRole, AccountStatus, Entitlements, PlanId } from '../domain'

export interface AdminStatsResponse {
  accountsByStatus: Record<string, number>
  accountsByKind: Record<string, number>
  companies: number
  users: number
  connectionsByStatus: Record<string, number>
  autopilot24h: { total: number; succeeded: number; failed: number; blocked: number }
  ai24h: { requests: number; errors: number; costUsd: number }
}

export interface AdminAccountRow {
  id: string
  name: string
  kind: AccountKind
  status: AccountStatus
  planId: string | null
  companies: number
  ownerEmail: string | null
  createdAt: string
}

/** GET /admin/accounts?q= */
export interface AdminAccountsResponse {
  items: AdminAccountRow[]
}

export interface AdminConnectionRow {
  id: string
  companyId: string
  companyName: string
  provider: string
  externalAccountId: string
  externalAccountName: string | null
  status: string
  health: unknown
  lastSyncAt: string | null
  lastError: string | null
}

export interface AdminAuditRow {
  id: string
  at: string
  action: string
  actorKind: 'user' | 'system' | 'staff'
  actorEmail: string | null
  accountId: string | null
  companyId: string | null
  target: unknown
  ip: string | null
}

export interface AdminOverrideRow {
  key: string
  value: unknown
  reason: string | null
  createdAt: string
}

/** GET /admin/accounts/:id */
export interface AdminAccountDetail {
  account: { id: string; name: string; kind: AccountKind; status: AccountStatus; createdAt: string }
  subscription: { planId: string; status: string; trialEndsAt: string | null; currentPeriodEnd: string | null; provider: string; extraCompanies: number } | null
  entitlements: Entitlements & { planId: PlanId | null }
  overrides: AdminOverrideRow[]
  companies: Array<{ id: string; name: string; status: string; createdAt: string }>
  members: Array<{ userId: string; email: string; name: string; role: AccountRole; lastLoginAt: string | null }>
  connections: AdminConnectionRow[]
  recentActions: Array<{ id: string; companyId: string; origin: string; status: string; summary: string; createdAt: string; error: string | null }>
  recentAudit: AdminAuditRow[]
}

export const OVERRIDABLE_KEYS = [
  'maxCompanies', 'maxUsers', 'managedSpendPerCompany', 'autopilotModes', 'aiCreditsPerCompany',
  'weeklyIdeas', 'whiteLabel', 'clientPortal', 'syncIntervalMinutes',
] as const satisfies ReadonlyArray<keyof Entitlements>
export type OverridableKey = (typeof OVERRIDABLE_KEYS)[number]

/** PUT /admin/accounts/:id/overrides  (value null remove o override) */
export interface AdminOverrideUpdate {
  key: OverridableKey
  value: unknown
  reason: string
}

/** GET /admin/queues */
export interface AdminQueuesResponse {
  mode: 'bullmq' | 'inline'
  /** `oldestWaitingSec`: idade do job mais antigo esperando (null = fila sem espera). */
  lanes: Record<string, { waiting?: number; active?: number; delayed?: number; failed?: number; completed?: number; oldestWaitingSec?: number | null }>
  error: string | null
}

export interface AdminFlagRow {
  key: string
  label: string
  group: 'killswitch' | 'rule' | 'other'
  /** Kill switch: ligado = recurso desligado para todos */
  enabled: boolean
  known: boolean
  updatedAt: string | null
}

/** GET /admin/flags */
export interface AdminFlagsResponse {
  items: AdminFlagRow[]
}

export interface AiUsageRow {
  day: string
  task: string
  provider: string
  model: string
  requests: number
  errors: number
  inputTokens: number
  outputTokens: number
  costUsd: number
}

/** GET /admin/ai/usage?days= */
export interface AdminAiUsageResponse {
  days: number
  totals: { requests: number; errors: number; costUsd: number; inputTokens: number; outputTokens: number }
  rows: AiUsageRow[]
  topAccounts: Array<{ accountId: string | null; accountName: string | null; requests: number; costUsd: number }>
}

export const AI_PROVIDERS = ['anthropic', 'openai', 'xai', 'fake'] as const
export type AiProviderName = (typeof AI_PROVIDERS)[number]

export interface AiRouteDto {
  task: string
  provider: AiProviderName
  model: string
  effort: string | null
  maxOutputTokens: number
  fallbackProvider: AiProviderName | null
  fallbackModel: string | null
  enabled: boolean
  updatedAt?: string
}

/** GET /admin/ai/routes ; PUT /admin/ai/routes { routes } */
export interface AdminAiRoutesResponse {
  items: AiRouteDto[]
}

/** GET /admin/audit?accountId=&limit= */
export interface AdminAuditResponse {
  items: AdminAuditRow[]
}

/** POST /admin/connections/:id/resync */
export interface AdminResyncResponse {
  queued: boolean
  message: string
}

/** GET /admin/site-leads: contatos do formulário do site institucional (últimos 200) */
export interface AdminSiteLeadRow {
  id: string
  email: string
  name: string | null
  kind: 'business' | 'agency' | 'other'
  message: string | null
  createdAt: string
}
export interface AdminSiteLeadsResponse {
  items: AdminSiteLeadRow[]
}
